<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="cotizacion"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    />
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de cotizaciones</custom-title>
      </template>
      <template #right>
        <el-tooltip
          v-if="!canEdit"
          content="El usuario no posee permisos de edición"
          placement="top"
        >
          <el-button
            type="primary"
            size="default"
            :icon="IconAdd"
            disabled
          >
            Nueva cotización
          </el-button>
        </el-tooltip>

        <template v-else>
          <custom-dropdown-button
            v-if="canCreate && habilitarCotizacionesMasivas"
            label="Nueva cotización"
            @click="() => (modalVisible = true)"
          >
            <el-dropdown-item @click="() => (modalVisible = true)"
              >Crear nueva cotización</el-dropdown-item
            >
            <el-dropdown-item @click="() => (modalMasivaVisible = true)"
              >Crear múltiples cotizaciones</el-dropdown-item
            >
          </custom-dropdown-button>
          <el-button
            v-else-if="canCreate"
            type="primary"
            size="default"
            :icon="IconAdd"
            @click="() => (modalVisible = true)"
            >Nueva cotización</el-button
          >
          <modal-select-product-type
            v-model="modalVisible"
            title="Nueva cotización"
            url="/page/prices/edit"
            @close-modal="() => (modalVisible = false)"
          />
        </template>
      </template>
    </main-buttons-bar>
    <prices-filters />
    <custom-table
      :requestApi="findAllCotizacionesHTTP"
      :pathEdit="'/page/prices/edit'"
      :paramId="'Codigo'"
      :data="cotizaciones"
      :columns="columns"
      :showDetailButton="false"
      :requestArgs="filters"
      :fixedActions="'right'"
    >
      <template #custom-actions="{ row }">
        <el-tooltip
          :content="getTooltipMessage(canEdit, row)"
          placement="top"
        >
          <span>
            <el-button
              :disabled="!canEdit || row.Estado !== 'Con Propuesta'"
              :icon="IconCheck"
              size="small"
              @click.prevent="priceConfirmation(row)"
            />
          </span>
        </el-tooltip>
      </template>
    </custom-table>

    <modal-price-confirmation
      v-model="modalVisiblePriceConfirmation"
      :selectedRow="selectedPriceRow"
      @closed="modalVisiblePriceConfirmation = false"
    >
    </modal-price-confirmation>

    <el-dialog
      v-model="modalMasivaVisible"
      title="Crear múltiples cotizaciones"
      width="90%"
      top="4vh"
      destroy-on-close
      @closed="firstCotizaciones"
    >
      <cotizacion-masiva-setting />
    </el-dialog>
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
  computed,
} from 'vue';
import { storeToRefs } from 'pinia';
import { usePricesStore } from '../stores/prices_store.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import PricesFilters from './prices_filter.vue';
import ModalSelectProductType from '@/shared/components/modal_select_product_type.vue';
import ModalPriceConfirmation from './modal_price_confirmation.vue';
import CustomDropdownButton from '@/shared/components/custom_dropdown_button.vue';
import CotizacionMasivaSetting from '../cotizacion_masiva/cotizacion_masiva_setting.vue';
import IconAdd from '~icons/mdi/add';
import IconCheck from '~icons/mdi/check-bold';
import {
  tieneAcceso,
  RolesCotizaciones,
} from '@/enum/roles_enum.js';
import { useAuthStore } from '@/views/auth/auth_store.js';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import { ElNotification } from 'element-plus';
import PermissionGate from '@/shared/components/permission_gate.vue';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';

const configuracionStore = useConfiguracionStore();
const { habilitarCotizacionesMasivas } = storeToRefs(configuracionStore);
const pricesStore = usePricesStore();
const { cotizaciones, cotizacion, filters } = storeToRefs(pricesStore);
const { firstCotizaciones, findAllCotizacionesHTTP, findCotizacionPorCodigoHTTP } = pricesStore;
const modalVisible = ref(false);
const modalMasivaVisible = ref(false);
const authStore = useAuthStore();
const usuario = authStore.usuario();

const canCreate = computed(() => tieneAcceso(RolesCotizaciones, usuario));

const items = [
  { label: 'Inicio', to: '/dashboard', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const columns = [
  { field: 'Codigo', label: 'Código', width: 110, sortable: true, isVisible: true },
  { field: 'Cliente', label: 'Cliente', width: 240, sortable: true, isVisible: true },
  { field: 'Estado', label: 'Estado', width: 130, sortable: true, isVisible: true },
  { field: 'Producto', label: 'Producto', width: 280, sortable: true, isVisible: true },
  {
    field: 'Cantidad',
    label: 'Cantidad',
    width: 180,
    sortable: true,
    isVisible: true,
    isNumber: true,
    precision: 0,
  },
  { field: 'UnidadVenta', label: 'Unidad Venta', width: 130, sortable: true, isVisible: true },
  {
    field: 'CodigosEquivalentes',
    label: 'Código Equivalente',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'CodigosAdicionales',
    label: 'Código adicional',
    width: 180,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'FechaCreacion',
    label: 'Fecha Creacion',
    width: 130,
    sortable: true,
    isVisible: true,
    isDate: true,
  },
  { field: 'TipoProducto', label: 'Tipo producto', width: 220, sortable: false, isVisible: true },
];

const selectedPriceRow = ref(null);
const modalVisiblePriceConfirmation = ref(false);

const priceConfirmation = async (row) => {
  if (row && row.Estado === 'Con Propuesta' && +row.Codigo > 0) {
    await findCotizacionPorCodigoHTTP(row.Codigo);
    selectedPriceRow.value = row;
    modalVisiblePriceConfirmation.value = true;
    cotizacion.value.EstadoSolicitud.Codigo = EstadoSolicitud.ACEPTADA;
    cotizacion.value.EstadoSolicitud.Descripcion = 'Aceptada';
    cotizacion.value.CodigoEstado = EstadoSolicitud.ACEPTADA;
  } else {
    ElNotification({
      title: 'Gestionar cotizacion',
      message: 'Debe tener una propuesta comercial generada para poder aprobar.',
      type: 'warning',
    });
  }
};

const getTooltipMessage = (canEdit, row) => {
  if (!canEdit) {
    return 'El usuario no posee permisos de edición';
  }

  switch (row.Estado) {
    case 'Con Propuesta':
      return 'Confirmar cotización';
    case 'Aceptada':
      return 'Esta cotización ya se encuentra aceptada';
    case 'Rechazada':
      return 'Esta cotización ya se encuentra rechazada';
    default:
      return 'Debe tener una propuesta para poder aprobar';
  }
};

onBeforeMount(async () => {
  await firstCotizaciones();
});
</script>

<style scoped></style>
